import { useEffect, useRef } from 'react';

import {
	stringifyPersonalizationAttributes,
	type PersonalizationAttribute,
} from '@confluence/legacy-schema-bridge/entry-points/stringifyPersonalizationAttributes.ts';
import { useAtlassianAccountTraits } from '@confluence/targeting/entry-points/useAtlassianAccountTraits.ts';

import type {
	PersonalizationQuery as PersonalizationQueryData,
	PersonalizationQueryVariables,
} from './useOnboardingEligible/__types__/UserPersonalizationQuery.ts';

interface PersonalizationResponse {
	attributes: PersonalizationAttribute[] | null;
}

const mapPersonalizationResponse = (
	response: PersonalizationResponse,
): PersonalizationQueryData => ({
	experimentalUserPersonalization: {
		attributes: stringifyPersonalizationAttributes(response.attributes),
	},
});

export const useExperimentalUserPersonalizationQuery = ({
	skip,
	onError,
	variables,
}: {
	skip?: boolean;
	onError?: (error: Error) => void;
	variables: PersonalizationQueryVariables;
}): {
	data: PersonalizationQueryData | undefined;
	loading: boolean;
	error: Error | undefined;
} => {
	const lastHandledTraitErrorRef = useRef<Error | null>(null);

	const atlassianAccountTraitsResult = useAtlassianAccountTraits({
		skipBootstrap: skip || !variables.userId,
	});

	useEffect(() => {
		const traitError = atlassianAccountTraitsResult.error;

		if (
			skip ||
			!variables.userId ||
			!traitError ||
			lastHandledTraitErrorRef.current === traitError
		) {
			return;
		}

		lastHandledTraitErrorRef.current = traitError;
		onError?.(traitError);
	}, [atlassianAccountTraitsResult.error, onError, skip, variables.userId]);

	if (skip || !variables.userId) {
		return {
			data: undefined,
			loading: false,
			error: undefined,
		};
	}

	return {
		data: atlassianAccountTraitsResult.data
			? mapPersonalizationResponse(atlassianAccountTraitsResult.data)
			: undefined,
		loading: atlassianAccountTraitsResult.loading,
		error: atlassianAccountTraitsResult.error ?? undefined,
	};
};
